/**
 * brandHost.js — is this page being served as an INSTITUTE's, or as ours?
 *
 * An institute's subdomain (abc.lmscampus.com), its custom domain
 * (lms.abc.com) and a dev subdomain (abc.localhost) all belong to one
 * institute. On those hosts nothing may ever say "LMS Campus" — not the tab
 * title, not the logo, not for one frame while the branding request is in
 * flight. Until the institute's own branding is known, the honest answer is
 * nothing at all.
 *
 * Our own hosts — the apex, www., lms., app. and plain localhost — are where
 * the platform's name is the right one.
 *
 * ⚠️ public/index.html carries a copy of `isPlatformHost` in its inline boot
 * script, because that runs before this bundle exists. The two must agree;
 * `__tests__/utils/brandHost.test.js` runs the inline copy against the same
 * table of hosts as this one.
 */
import { isEnterprise } from '../constants/edition';

// The signed-in user, read the way utils/auth stores it. Not imported from
// there: auth pulls in the API client, and this module must stay cheap enough
// to run before anything else (BrandingContext's very first render).
const storedUser = () => {
  const raw = localStorage.getItem('user');
  return raw ? JSON.parse(raw) : null;
};

// The platform's own domain — the one place the web app names it. Production
// builds leave REACT_APP_BASE_DOMAIN unset; the QA build sets `qa.lmscampus.com`
// (`npm run build:qa`), so abc.qa.lmscampus.com is institute "abc" and
// lms.qa.lmscampus.com is ours. ⚠️ public/index.html reads the same variable.
export const BASE_DOMAIN = String(process.env.REACT_APP_BASE_DOMAIN || 'lmscampus.com').toLowerCase();

// The platform's own names under the base domain. Everything else under it is
// an institute's subdomain.
export const PLATFORM_SUBDOMAINS = ['www', 'lms', 'app', 'api', 'admin'];

/** The base domain itself, or any host under it — ours or an institute's subdomain. */
export function isUnderBaseDomain(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === BASE_DOMAIN || h.endsWith(`.${BASE_DOMAIN}`);
}

/** The host one label under the base domain: `baseHost('lms')` → lms.lmscampus.com. */
export function baseHost(sub) {
  return sub ? `${sub}.${BASE_DOMAIN}` : BASE_DOMAIN;
}

/** True for our own hosts; false for an institute's subdomain or custom domain. */
export function isPlatformHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h || h === 'localhost' || h === '127.0.0.1') return true;
  if (h.endsWith('.localhost')) return false;
  if (h === BASE_DOMAIN) return true;
  if (h.endsWith(`.${BASE_DOMAIN}`)) {
    const sub = h.slice(0, -(BASE_DOMAIN.length + 1));
    return PLATFORM_SUBDOMAINS.includes(sub);
  }
  return false;
}

/**
 * Should this page wear an institute's brand rather than ours?
 *
 *   - an institute's host, always;
 *   - our own host with an institute user signed in (a student who logs in at
 *     lms.lmscampus.com sees their institute, not us).
 *
 * An enterprise build is never "an institute's host": the operator's brand is
 * baked into the bundle (constants/brand) and is the right thing to show from
 * the first frame.
 */
export function isInstituteContext(hostname = window.location.hostname) {
  if (isEnterprise) return false;
  if (!isPlatformHost(hostname)) return true;
  try {
    const user = storedUser();
    return Boolean(user && user.usertype !== 'tenantadmin' && user.instituteId);
  } catch {
    return false;
  }
}

/** A 1×1 transparent PNG — the favicon while an institute's is not known yet. */
export const BLANK_FAVICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
